import React from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import moment from 'moment';
import { Box, Group, Title } from '@cowbell/ui';

import { useAiCowbellFactorsHistory } from '../../components/hooks/useAiCowbellFactors';
import classes from './AiCFHistory.module.css';

const SERIES = [
  { key: 'exposure', name: 'AI Exposure', color: '#6baedf' },
  { key: 'vulnerability', name: 'AI Vulnerability', color: '#7581e5' },
  { key: 'assurance', name: 'AI Assurance', color: '#00aea0' },
] as const;

// Deliberately independent of the shared date range: that picker caps at
// 2023-12-31 while Cowbell Factors v2 is on, which would filter out every AI
// factor run, since those are current-year data.
export const AiCFHistory = ({ accountId }: Readonly<{ accountId: string }>) => {
  const query = useAiCowbellFactorsHistory(accountId);

  if (!query.data?.length) {
    return null;
  }

  // Highcharts line series connect in array order, not by x-value, so an
  // unsorted payload would draw a zigzag instead of a time series.
  const chronologicalRuns = [...query.data].sort(
    (older, newer) =>
      moment.utc(older.generatedAt).valueOf() -
      moment.utc(newer.generatedAt).valueOf()
  );

  const axisLabels = { style: { color: 'var(--mantine-color-text)' } };

  const options: Highcharts.Options = {
    chart: {
      type: 'line',
      height: 165,
      backgroundColor: 'var(--mantine-color-body)',
    },
    title: { text: undefined },
    credits: { enabled: false },
    // The swatches below the header are the only legend this chart shows.
    legend: { enabled: false },
    xAxis: { type: 'datetime', labels: axisLabels },
    yAxis: {
      min: 0,
      max: 100,
      title: { text: undefined },
      labels: axisLabels,
    },
    tooltip: {
      shared: true,
      valueSuffix: ' / 100',
    },
    series: SERIES.map((series) => ({
      type: 'line',
      name: series.name,
      color: series.color,
      data: chronologicalRuns.map((run) => [
        moment.utc(run.generatedAt).valueOf(),
        run[series.key],
      ]),
    })),
  };

  return (
    <Box
      component="section"
      className={classes.container}
      data-testid="ai-cowbell-factors-history"
    >
      <Title className={classes.header} order={3}>
        AI COWBELL FACTORS - HISTORY
      </Title>
      <Group className={classes.legend}>
        {SERIES.map((series) => (
          <Group key={series.key} gap="0.4rem">
            <Box
              className={`${classes.swatch} ${classes[series.key]}`}
              aria-hidden
            />
            {series.name}
          </Group>
        ))}
      </Group>
      <HighchartsReact highcharts={Highcharts} options={options} />
    </Box>
  );
};
